import type { LucideIcon } from "lucide-react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface KpiCardProps {
    icon: LucideIcon;
    iconColor?: string;
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    delta?: string | null;
    deltaDir?: "up" | "down" | "flat";
    onClick?: () => void;
}

export function KpiCard({
    icon: Icon,
    iconColor = "text-blue-500",
    label,
    value,
    sub,
    delta,
    deltaDir = "up",
    onClick,
}: KpiCardProps) {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            onClick={onClick}
            className={cn(
                "bg-white rounded-xl border border-gray-100 p-5 text-left w-full",
                onClick &&
                    "hover:shadow-md hover:border-blue-200 cursor-pointer transition-all"
            )}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="bg-gray-50 p-2 rounded-lg">
                    <Icon className={cn("w-5 h-5", iconColor)} />
                </div>
                {delta && deltaDir !== "flat" && (
                    <span
                        className={cn(
                            "inline-flex items-center gap-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full",
                            deltaDir === "up"
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-rose-50 text-rose-700"
                        )}
                    >
                        {deltaDir === "up" ? (
                            <TrendingUp className="w-3 h-3" />
                        ) : (
                            <TrendingDown className="w-3 h-3" />
                        )}
                        {delta}
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-500 uppercase font-medium tracking-wider">
                {label}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1 font-mono tabular-nums">
                {value}
            </p>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </Tag>
    );
}
